import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  VIP_SERVER_ERROR_CODE,
  type VipServerErrorCode,
} from '../components/VipTransferFormCard/analytics';
import {
  isVipTransferRequestError,
  sendVipTransferRequest,
} from '../components/VipTransferFormCard/api';

const EMAIL_SEND_FAILED_STATUS = 502;

const isNetworkError = (error: unknown): boolean => error instanceof TypeError;

const getRawErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const getServerErrorCode = (error: unknown): VipServerErrorCode => {
  if (isNetworkError(error)) return VIP_SERVER_ERROR_CODE.networkError;
  if (
    isVipTransferRequestError(error) &&
    error.status === EMAIL_SEND_FAILED_STATUS
  ) {
    return VIP_SERVER_ERROR_CODE.emailNotSent;
  }
  return VIP_SERVER_ERROR_CODE.serverError;
};

const REQUEST_ERROR_KEY_BY_KIND = {
  network: 'steps.vipTransfer.transferForm.errorNetwork',
  requestNotSent: 'steps.vipTransfer.transferForm.errorRequestNotSent',
} as const;

export type VipTransferRequestResult =
  | { status: 'success' }
  | {
      status: 'error';
      /** Localized message shown to the user. */
      error: string;
      /** GA taxonomy code for analytics. */
      code: VipServerErrorCode;
      /** Raw backend/network error message, when available. */
      rawError?: string;
    };

interface IUseVipTransferRequestResult {
  sendRequest: (files: File[]) => Promise<VipTransferRequestResult>;
  isRequestPending: boolean;
}

export const useVipTransferRequest = (): IUseVipTransferRequestResult => {
  const { t } = useTranslation('vip');
  const [isRequestPending, setIsRequestPending] = useState(false);

  const sendRequest = useCallback(
    async (files: File[]): Promise<VipTransferRequestResult> => {
      setIsRequestPending(true);

      try {
        await sendVipTransferRequest(files);
        return { status: 'success' };
      } catch (error) {
        console.error('VIP transfer request failed:', error);
        const code = getServerErrorCode(error);
        const key =
          REQUEST_ERROR_KEY_BY_KIND[
            code === VIP_SERVER_ERROR_CODE.networkError
              ? 'network'
              : 'requestNotSent'
          ];
        return {
          status: 'error',
          error: t(key),
          code,
          rawError: getRawErrorMessage(error),
        };
      } finally {
        setIsRequestPending(false);
      }
    },
    [t]
  );

  return { sendRequest, isRequestPending };
};
